/**
 * ContratsBatimentsPanel — contrats & baux des bâtiments (item 7.5, mig 271).
 *
 * ─── LA QUESTION À LAQUELLE CET ÉCRAN RÉPOND ────────────────────────────────
 *
 * Pas « quels contrats avons-nous ? » mais **« lequel dois-je dénoncer, et
 * avant quand ? »**. C'est pour ça qu'il ouvre sur les échéances les plus
 * proches et non sur un ordre alphabétique.
 *
 * ─── LA DATE AFFICHÉE N'EST PAS LA FIN DU CONTRAT ───────────────────────────
 *
 * Avec reconduction tacite, c'est la LIMITE DE DÉNONCIATION (fin − préavis)
 * qui compte : passé cette date, le contrat est reparti pour un tour même s'il
 * reste des mois avant son terme. L'écran affiche donc cette date en premier,
 * et rappelle le terme en second — l'inverse ferait manquer l'échéance utile.
 *
 * La date est calculée EN BASE (colonne générée) : le front l'affiche, il ne
 * la recalcule jamais. Aucun `toISOString()` ici, charte du projet.
 *
 * ─── DEUX BRIQUES, UN SEUL ÉCRAN, DEUX DROITS ───────────────────────────────
 *
 * Le service technique gère ; le comptable, lui, consulte depuis SA brique
 * (`comptable.contrat.read`) sans recevoir la moindre permission
 * `service_technique.*`. `peutModifier` porte cette différence : sans droit
 * d'écriture, l'écran reste utile mais n'affiche aucun bouton d'action —
 * plutôt qu'un bouton qui renverrait un 403.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Badge, Alert, Button, Input, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type {
  BatimentContrat, BatimentContratInput, ContratCategorie,
  ContratPeriodicite, ContratSens, AnnuaireTiers,
} from '@roronoa-collectivites/shared-types';
// ⚠️ `LieuDTO` est déclaré dans `api.ts`, PAS dans shared-types — vérifié après
// l'échec de typecheck du CI (29/07). Je l'avais supposé partagé.
import type { LieuDTO } from '@/lib/api';
import { FileSignature, Plus, Info, AlertTriangle, Trash2, Pencil } from 'lucide-react';

const LIB_CATEGORIE: Record<ContratCategorie, string> = {
  bail: 'Bail',
  maintenance: 'Maintenance',
  assurance: 'Assurance',
  energie: 'Énergie',
  telecom: 'Télécom',
  occupation_precaire: 'Occupation précaire',
  aot: 'AOT domaine public',
  prestation: 'Prestation',
  autre: 'Autre',
};

const LIB_PERIODICITE: Record<ContratPeriodicite, string> = {
  mensuel: 'Mensuel', trimestriel: 'Trimestriel', semestriel: 'Semestriel',
  annuel: 'Annuel', unique: 'Paiement unique',
};

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

/**
 * Jours restants, calculés sur des composants de date locaux — jamais sur un
 * horodatage UTC. C'est la même précaution que côté serveur : à minuit près,
 * un décalage de fuseau fait afficher « en retard » un contrat qui ne l'est pas.
 */
function joursRestants(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const cible = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((cible.getTime() - today.getTime()) / 86_400_000);
}

function EcheanceBadge({ c }: { c: BatimentContrat }) {
  if (!c.dateSurveillee) {
    return (
      <Badge variant="neutral" title="Contrat sans terme connu : rien à décider à une date fixe">
        Sans terme
      </Badge>
    );
  }
  const j = joursRestants(c.dateSurveillee);
  if (j === null) return <Badge variant="neutral">—</Badge>;
  if (j < 0) {
    return (
      <Badge
        variant="urgent"
        title={c.reconductionTacite
          ? 'La date limite de dénonciation est passée : le contrat s’est reconduit.'
          : 'Le contrat est arrivé à son terme.'}
      >
        {c.reconductionTacite ? 'Reconduit' : 'Échu'} — {Math.abs(j)} j
      </Badge>
    );
  }
  if (j <= 30) return <Badge variant="urgent">Dans {j} j</Badge>;
  if (j <= 120) return <Badge variant="warning">Dans {j} j</Badge>;
  return <Badge variant="success">Dans {Math.round(j / 30)} mois</Badge>;
}

const VIDE: BatimentContratInput = {
  libelle: '', sens: 'depense', categorie: 'maintenance',
  tiersId: null, reference: null, dateDebut: '', dateFin: null,
  reconductionTacite: false, preavisMois: null, montant: null,
  periodicite: null, revisionIndice: null, notes: null,
  actif: true, lieuIds: [],
};

export function ContratsBatimentsPanel() {
  const { user } = useAuth();
  // `service_technique.contrat.manage` est accordée aux rôles super_admin,
  // admin et manager (mig 271) ; côté serveur, `requireServiceMiddleware`
  // ajoute la garde de brique pour le manager. On reproduit LA MÊME règle ici,
  // sinon l'écran afficherait des boutons qui renverraient 403.
  //
  // ⚠️ Pas de `hasPermission()` : `auth-context.tsx` n'expose pas la matrice de
  // permissions au front. Le rôle et le service sont ce dont on dispose.
  const peutModifier = !!user && (
    user.role === 'super_admin'
    || user.role === 'admin'
    || (user.role === 'manager'
        && (user.service === 'service_technique'
            || user.servicesAdditionnels.includes('service_technique')))
  );
  const { confirm, dialogElement } = useConfirmDialog();

  const [contrats, setContrats] = useState<BatimentContrat[]>([]);
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [tiers, setTiers] = useState<AnnuaireTiers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [filtreSens, setFiltreSens] = useState<'' | ContratSens>('');
  const [filtreCategorie, setFiltreCategorie] = useState<'' | ContratCategorie>('');
  const [inactifs, setInactifs] = useState(false);

  const [edition, setEdition] = useState<{ id: string | null; data: BatimentContratInput } | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.admin.lieux.contrats({
        sens: filtreSens || undefined,
        categorie: filtreCategorie || undefined,
        inactifs,
      });
      setContrats(r.contrats);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [filtreSens, filtreCategorie, inactifs]);

  useEffect(() => { void charger(); }, [charger]);

  // Référentiels du formulaire, chargés une seule fois. Une erreur ici ne doit
  // PAS masquer la liste : un comptable sans droit sur le référentiel des
  // lieux garde un écran parfaitement lisible.
  useEffect(() => {
    void (async () => {
      try { setLieux((await api.admin.lieux.list()).lieux); } catch { /* liste seule */ }
      try { setTiers(await api.annuaire.listTiers()); } catch { /* liste seule */ }
    })();
  }, []);

  const enRetard = useMemo(
    () => contrats.filter((c) => {
      const j = joursRestants(c.dateSurveillee);
      return j !== null && j < 0;
    }).length,
    [contrats],
  );

  async function enregistrer() {
    if (!edition) return;
    const d = edition.data;
    if (!d.libelle.trim() || !d.dateDebut) {
      setError('Le libellé et la date de début sont obligatoires.');
      return;
    }
    if (d.reconductionTacite && (!d.dateFin || !d.preavisMois)) {
      setError('Une reconduction tacite exige une date de fin ET un préavis : sans eux, personne ne sera prévenu à temps.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const saved = edition.id
        ? await api.admin.lieux.majContrat(edition.id, d)
        : await api.admin.lieux.creerContrat(d);
      setMsg(
        saved.dateLimiteDenonciation
          ? `Contrat enregistré. Dénonciation à notifier avant le ${fr(saved.dateLimiteDenonciation)} — un rappel est programmé.`
          : saved.dateFin
            ? `Contrat enregistré. Terme le ${fr(saved.dateFin)} — un rappel est programmé.`
            : 'Contrat enregistré. Sans date de fin, aucun rappel n’est programmé.',
      );
      setEdition(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function supprimer(c: BatimentContrat) {
    const ok = await confirm({
      title: 'Supprimer ce contrat ?',
      description: `« ${c.libelle} » et son rappel d’échéance seront supprimés. Pour garder la trace d’un contrat terminé, décochez plutôt « actif ».`,
      danger: true,
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await api.admin.lieux.supprimerContrat(c.id);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  function ouvrir(c?: BatimentContrat) {
    if (!c) { setEdition({ id: null, data: { ...VIDE } }); return; }
    setEdition({
      id: c.id,
      data: {
        libelle: c.libelle, sens: c.sens, categorie: c.categorie,
        tiersId: c.tiersId, reference: c.reference,
        dateDebut: c.dateDebut, dateFin: c.dateFin,
        reconductionTacite: c.reconductionTacite, preavisMois: c.preavisMois,
        montant: c.montant, periodicite: c.periodicite,
        revisionIndice: c.revisionIndice, notes: c.notes,
        actif: c.actif, lieuIds: c.lieux.map((l) => l.id),
      },
    });
  }

  const maj = (patch: Partial<BatimentContratInput>) =>
    setEdition((e) => (e ? { ...e, data: { ...e.data, ...patch } } : e));

  const champ = 'h-10 w-full rounded-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <FileSignature className="h-4 w-4 text-tech" aria-hidden="true" />
            Contrats &amp; baux des bâtiments
          </h3>
          {peutModifier && (
            <Button size="sm" onClick={() => ouvrir()}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau contrat
            </Button>
          )}
        </div>

        <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            La date suivie n’est pas le terme du contrat mais la <strong>limite de
            dénonciation</strong> (terme moins préavis) : au-delà, un contrat à
            reconduction tacite est reparti pour un tour. Le rappel part sur cette
            date-là.
          </span>
        </p>

        {enRetard > 0 && (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            {enRetard === 1
              ? '1 contrat a dépassé sa date de décision.'
              : `${enRetard} contrats ont dépassé leur date de décision.`}
          </Alert>
        )}

        {/* ── Filtres ── */}
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div>
            <label className={label} htmlFor="f-sens">Sens</label>
            <select id="f-sens" className={champ} value={filtreSens}
              onChange={(e) => setFiltreSens(e.target.value as '' | ContratSens)}>
              <option value="">Tous</option>
              <option value="depense">La commune paie</option>
              <option value="recette">La commune encaisse</option>
            </select>
          </div>
          <div>
            <label className={label} htmlFor="f-cat">Nature</label>
            <select id="f-cat" className={champ} value={filtreCategorie}
              onChange={(e) => setFiltreCategorie(e.target.value as '' | ContratCategorie)}>
              <option value="">Toutes</option>
              {(Object.keys(LIB_CATEGORIE) as ContratCategorie[]).map((k) => (
                <option key={k} value={k}>{LIB_CATEGORIE[k]}</option>
              ))}
            </select>
          </div>
          <label className="flex h-10 items-center gap-2 text-xs text-ink-muted">
            <input type="checkbox" checked={inactifs}
              onChange={(e) => setInactifs(e.target.checked)} />
            Afficher aussi les contrats clos
          </label>
        </div>

        {loading ? (
          <p className="py-6 text-center text-sm text-ink-subtle">Chargement…</p>
        ) : contrats.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtle">
            Aucun contrat enregistré. Commencez par les baux et les contrats de
            maintenance : ce sont eux qui se reconduisent sans prévenir.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {contrats.map((c) => (
              <li key={c.id} className="flex flex-wrap items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{c.libelle}</span>
                    <Badge variant="neutral" size="sm">{LIB_CATEGORIE[c.categorie]}</Badge>
                    <Badge variant={c.sens === 'recette' ? 'success' : 'neutral'} size="sm">
                      {c.sens === 'recette' ? 'Recette' : 'Dépense'}
                    </Badge>
                    {!c.actif && <Badge variant="neutral" size="sm">Clos</Badge>}
                  </div>

                  <p className="mt-1 text-xs text-ink-muted">
                    {c.tiersNom ?? 'Cocontractant non renseigné'}
                    {c.reference ? ` · réf. ${c.reference}` : ''}
                    {c.montant !== null
                      ? ` · ${c.montant.toLocaleString('fr-FR')} €${c.periodicite ? ` / ${LIB_PERIODICITE[c.periodicite].toLowerCase()}` : ''}`
                      : ''}
                  </p>

                  <p className="mt-1 text-xs text-ink-muted">
                    {c.dateLimiteDenonciation ? (
                      <>
                        <strong>Dénoncer avant le {fr(c.dateLimiteDenonciation)}</strong>
                        {' '}· terme {fr(c.dateFin)} · préavis {c.preavisMois} mois
                      </>
                    ) : c.dateFin ? (
                      <>Terme le {fr(c.dateFin)} · pas de reconduction tacite</>
                    ) : (
                      <>Depuis le {fr(c.dateDebut)} · sans terme</>
                    )}
                  </p>

                  {c.lieux.length > 0 && (
                    <p className="mt-1 text-xs text-ink-subtle">
                      {c.lieux.map((l) => l.nom).join(' · ')}
                    </p>
                  )}
                  {c.lieux.length === 0 && (
                    <p className="mt-1 text-xs text-ink-subtle">
                      Contrat communal — non rattaché à un bâtiment précis
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <EcheanceBadge c={c} />
                  {peutModifier && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => ouvrir(c)}
                        aria-label={`Modifier ${c.libelle}`}>
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void supprimer(c)}
                        aria-label={`Supprimer ${c.libelle}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── Formulaire ── */}
      {edition && (
        <Card variant="glass" padded>
          <h3 className="mb-3 text-sm font-semibold text-ink">
            {edition.id ? 'Modifier le contrat' : 'Nouveau contrat'}
          </h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Libellé" htmlFor="c-libelle" required>
              <Input id="c-libelle" value={edition.data.libelle}
                onChange={(e) => maj({ libelle: e.target.value })} />
            </Field>

            <Field label="Cocontractant" htmlFor="c-tiers"
              hint="Pris dans l’annuaire des tiers — jamais en saisie libre.">
              <select id="c-tiers" className={champ} value={edition.data.tiersId ?? ''}
                onChange={(e) => maj({ tiersId: e.target.value || null })}>
                <option value="">—</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </Field>

            <Field label="Sens" htmlFor="c-sens"
              hint="La commune paie, ou elle encaisse (logement communal loué, AOT).">
              <select id="c-sens" className={champ} value={edition.data.sens}
                onChange={(e) => maj({ sens: e.target.value as ContratSens })}>
                <option value="depense">La commune paie</option>
                <option value="recette">La commune encaisse</option>
              </select>
            </Field>

            <Field label="Nature" htmlFor="c-cat">
              <select id="c-cat" className={champ} value={edition.data.categorie}
                onChange={(e) => maj({ categorie: e.target.value as ContratCategorie })}>
                {(Object.keys(LIB_CATEGORIE) as ContratCategorie[]).map((k) => (
                  <option key={k} value={k}>{LIB_CATEGORIE[k]}</option>
                ))}
              </select>
            </Field>

            <Field label="Référence" htmlFor="c-ref"
              hint="N° de contrat ou de police — pour retrouver le papier.">
              <Input id="c-ref" value={edition.data.reference ?? ''}
                onChange={(e) => maj({ reference: e.target.value || null })} />
            </Field>

            <Field label="Date de début" htmlFor="c-debut" required>
              <Input id="c-debut" type="date" value={edition.data.dateDebut}
                onChange={(e) => maj({ dateDebut: e.target.value })} />
            </Field>

            <Field label="Terme du contrat" htmlFor="c-fin"
              hint="Vide = contrat sans terme (fourniture d’énergie) : aucun rappel.">
              <Input id="c-fin" type="date" value={edition.data.dateFin ?? ''}
                onChange={(e) => maj({ dateFin: e.target.value || null })} />
            </Field>

            <Field label="Préavis de dénonciation (mois)" htmlFor="c-preavis"
              hint="C’est lui qui décale la date à surveiller avant le terme.">
              <Input id="c-preavis" type="number" min={1} max={36}
                value={edition.data.preavisMois ?? ''}
                onChange={(e) => maj({ preavisMois: e.target.value ? Number(e.target.value) : null })} />
            </Field>

            <Field label="Montant (€)" htmlFor="c-montant">
              <Input id="c-montant" type="number" min={0} step="0.01"
                value={edition.data.montant ?? ''}
                onChange={(e) => maj({ montant: e.target.value ? Number(e.target.value) : null })} />
            </Field>

            <Field label="Périodicité" htmlFor="c-periodicite">
              <select id="c-periodicite" className={champ} value={edition.data.periodicite ?? ''}
                onChange={(e) => maj({ periodicite: (e.target.value || null) as ContratPeriodicite | null })}>
                <option value="">—</option>
                {(Object.keys(LIB_PERIODICITE) as ContratPeriodicite[]).map((k) => (
                  <option key={k} value={k}>{LIB_PERIODICITE[k]}</option>
                ))}
              </select>
            </Field>

            <Field label="Indice de révision" htmlFor="c-indice"
              hint="ILC, IRL, ICC… recopié du contrat.">
              <Input id="c-indice" value={edition.data.revisionIndice ?? ''}
                onChange={(e) => maj({ revisionIndice: e.target.value || null })} />
            </Field>
          </div>

          <label className="mt-3 flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" className="mt-1" checked={edition.data.reconductionTacite}
              onChange={(e) => maj({ reconductionTacite: e.target.checked })} />
            <span>
              Reconduction tacite
              <span className="block text-xs text-ink-muted">
                Si coché, le terme et le préavis deviennent obligatoires : c’est la seule
                façon de savoir avant quand dénoncer.
              </span>
            </span>
          </label>

          {/* Aperçu de la date réellement surveillée — le calcul définitif est
              fait en base ; celui-ci n'est qu'une aide à la saisie. */}
          {edition.data.reconductionTacite && edition.data.dateFin && edition.data.preavisMois && (
            <Alert variant="info" className="mt-3">
              À surveiller : la dénonciation devra être notifiée environ{' '}
              {edition.data.preavisMois} mois avant le {fr(edition.data.dateFin)}.
              La date exacte est calculée à l’enregistrement.
            </Alert>
          )}

          <fieldset className="mt-3">
            <legend className={label}>Bâtiments couverts</legend>
            <p className="mb-2 text-xs text-ink-muted">
              Aucun coché = contrat communal global (une assurance dommages aux biens,
              par exemple). Plusieurs coches = un même contrat couvre plusieurs bâtiments.
            </p>
            <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2">
              {lieux.length === 0 ? (
                <p className="text-xs text-ink-subtle">Aucun bâtiment au référentiel.</p>
              ) : lieux.map((l) => (
                <label key={l.id} className="flex items-center gap-2 py-0.5 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={edition.data.lieuIds.includes(l.id)}
                    onChange={(e) => maj({
                      lieuIds: e.target.checked
                        ? [...edition.data.lieuIds, l.id]
                        : edition.data.lieuIds.filter((x) => x !== l.id),
                    })}
                  />
                  {l.nom}
                </label>
              ))}
            </div>
          </fieldset>

          <Field label="Notes" htmlFor="c-notes">
            <textarea id="c-notes" rows={3}
              className="w-full rounded-md border border-border bg-surface/60 p-2 text-sm text-ink"
              value={edition.data.notes ?? ''}
              onChange={(e) => maj({ notes: e.target.value || null })} />
          </Field>

          <label className="mt-2 flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={edition.data.actif}
              onChange={(e) => maj({ actif: e.target.checked })} />
            Contrat actif (décocher clôt le suivi sans effacer l’historique)
          </label>

          <div className="mt-4 flex gap-2">
            <Button onClick={() => void enregistrer()} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="ghost" onClick={() => setEdition(null)} disabled={saving}>
              Annuler
            </Button>
          </div>
        </Card>
      )}

      {dialogElement}
    </div>
  );
}
